import { getMeta, setMeta } from "./db";

// Card #2829 (docs/score/scribe-resilience.md gap G13).
// `navigator.storage.persist()` is never called anywhere in `apps/` or
// `packages/`, so a best-effort origin's IndexedDB is a candidate for
// eviction under device storage pressure -- the browser can discard it with
// no warning and no user action. On a ring tablet holding the only copy of
// half a day of scores, that is total loss. Chrome auto-grants persistence
// to an installed PWA (scribe is installed, InstallButton.jsx) and to
// high-engagement origins, so many stations are probably already
// persistent -- we have simply never checked or recorded it.
//
// This module owns the measurement + the record; `useRequestStoragePersistence`
// (hooks/) owns calling it once at startup after sign-in.

export const STORAGE_PERSISTENCE_META_KEY = "storagePersistence";

const listeners = new Set();

// No consumer needs a push update mid-check today (TopBar mounts fresh on
// every route change and just reads the last recorded value), but the
// startup request and the chip that renders off it live in different
// component subtrees with no shared parent closer than App.jsx, so a plain
// module-level publish/subscribe (same shape as store/presenceLocation.js's
// plain-variable store, just with listeners) lets the chip update the moment
// the very first startup check resolves instead of waiting for its own next
// mount.
function publish(info) {
  for (const listener of listeners) {
    try {
      listener(info);
    } catch {
      // A listener's own error is that listener's problem, never ours.
    }
  }
}

export function subscribeStoragePersistence(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getStorage() {
  return typeof navigator !== "undefined" ? navigator.storage : undefined;
}

// Calls persist(), then measures persisted()/estimate() regardless of what
// persist() itself returned (a station already persisted from a previous
// visit, or auto-granted by the browser for being an installed PWA, never
// calls persist() again having already returned true the first time -- we
// still want persisted()'s own answer, not persist()'s, since that is the
// one question this whole card exists to stop leaving unanswered).
export async function requestAndRecordStoragePersistence() {
  const storage = getStorage();

  if (!storage || typeof storage.persist !== "function") {
    const info = {
      supported: false,
      persisted: false,
      refused: false,
      usage: null,
      quota: null,
      checkedAt: new Date().toISOString(),
    };
    await setMeta(STORAGE_PERSISTENCE_META_KEY, info);
    publish(info);
    return info;
  }

  try {
    await storage.persist();
  } catch {
    // A rejected/throwing persist() call still leaves persisted() below as
    // the source of truth for whether this origin actually ended up
    // persistent -- never let this branch decide the answer on its own.
  }

  let persisted = false;
  if (typeof storage.persisted === "function") {
    try {
      persisted = Boolean(await storage.persisted());
    } catch {
      persisted = false;
    }
  }

  let usage = null;
  let quota = null;
  if (typeof storage.estimate === "function") {
    try {
      const estimate = await storage.estimate();
      usage = typeof estimate?.usage === "number" ? estimate.usage : null;
      quota = typeof estimate?.quota === "number" ? estimate.quota : null;
    } catch {
      // estimate() unsupported or failing leaves usage/quota null; not fatal.
    }
  }

  const info = {
    supported: true,
    persisted,
    refused: !persisted,
    usage,
    quota,
    checkedAt: new Date().toISOString(),
  };
  await setMeta(STORAGE_PERSISTENCE_META_KEY, info);
  publish(info);
  return info;
}

export async function getStoragePersistenceInfo() {
  const info = await getMeta(STORAGE_PERSISTENCE_META_KEY);
  return info ?? null;
}
